import * as React from 'react'
import { Box } from '@mui/material'
import {
  List,
  TextInput,
  NumberInput,
  TopToolbar,
  SelectColumnsButton,
  FilterButton,
  DatagridConfigurable,
  TextField,
  DateInput,
  FunctionField,
  Link,
  SelectInput,
  DateField,
  useNotify,
  useDataProvider,
  useListContext,
} from 'react-admin'
import { isResultDetailsRestricted, labTestResultLabelWithNotes, ResultDetailPdf } from './Fields'
import { ViewTestResultAction } from './Actions'
import Empty from '../../common/Empty'
import StatusPill from '../../common/StatusPill'
import { KitStatusLabels } from '../../../utils/constants'
import { useProviderContext } from '../../../context/ProviderContext'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDateToMMDDYYYY } from '../../../utils/helpers'

// Carries the currently-loaded, filtered/sorted kit id order as route state
// so the Show page's prev/next nav can walk the same result set the provider
// clicked from, without an extra fetch (pagination is disabled on this List,
// so `data` here is already the full matching set).
const KitIdLink = ({ record }) => {
  const { data } = useListContext()
  const resultListIds = (data || []).map((kit) => kit.kitId)
  return (
    <Link
      to={`/provider-portal/results/${record.kitId}/show`}
      state={{ resultListIds }}
      style={{ color: 'var(--text-accent)', textDecoration: 'none' }}
    >
      {record.kitId}
    </Link>
  )
}

const ResultList = (props) => {
  const { id } = useParams()
  const { providerInfo, setProviderInfo } = useProviderContext()
  const [loading, setLoading] = React.useState(!providerInfo.id)

  const navigate = useNavigate()
  const notify = useNotify()
  const dataProvider = useDataProvider()

  React.useEffect(() => {
    if (providerInfo.id) {
      setLoading(false)
      return
    }

    if (!id) {
      navigate(-1)
      return
    }

    // Hydrate context from URL param by fetching providers list
    dataProvider
      .getList('provider-portal/providers', { filter: { _order: 'DESC' }, pagination: {}, sort: {} })
      .then(({ data }) => {
        const provider = data.find((p) => p.id === id)
        if (provider) {
          setProviderInfo({ id: provider.id, displayName: provider.displayName })
        } else {
          notify('Provider not found', { type: 'error' })
          navigate(-1)
        }
      })
      .catch(() => {
        notify('Failed to load provider info', { type: 'error' })
        navigate(-1)
      })
      .finally(() => setLoading(false))
  }, [providerInfo.id, id, navigate, dataProvider, setProviderInfo, notify])

  if (loading) {
    return null
  }

  const filterInputSx = {
    '& .MuiInputBase-root': { borderRadius: 'var(--radius)' },
  }

  const filterArr = [
    <NumberInput alwaysOn label="KitId" source="kitId" size="small" sx={filterInputSx} style={{ width: '250px' }} />,
    <TextInput
      alwaysOn
      label="Patient Name"
      source="patientName"
      resettable
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <DateInput alwaysOn label="DOB" source="patientDoB" size="small" sx={filterInputSx} />,
    <SelectInput
      alwaysOn
      label="Tested/Not Tested"
      source="showTestedKits"
      emptyText="All Kits"
      choices={[
        { id: 'true', name: 'Tested Kits' },
        { id: 'false', name: 'Not Tested Kits' },
      ]}
      resettable
      emptyOption={false}
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <SelectInput
      alwaysOn
      label="Result"
      source="result"
      emptyText="All Results"
      choices={[
        { id: 'none', name: 'Not Tested' },
        { id: 'Positive', name: 'Positive' },
        { id: 'NSS', name: 'NSS' },
        { id: 'error', name: 'Error' },
      ]}
      resettable
      emptyOption={false}
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
    <SelectInput
      alwaysOn
      emptyText="All"
      label="Acknowledgment"
      source="acknowledgment"
      choices={[
        { id: 'acknowledged', name: 'Acknowledged' },
        { id: 'unacknowledged', name: 'Un-Acknowledged' },
      ]}
      resettable
      emptyOption={false}
      size="small"
      sx={filterInputSx}
      style={{ width: '250px' }}
    />,
  ]

  // Full-row tint so an unacknowledged result is spottable at a glance while
  // skimming the list, not just by reading the 'Provider Ack'd' column.
  const resultRowStyle = (record) => ({
    backgroundColor: record.providerAcknowledgedAt > 0 ? '#efefef' : '#d9edf7',
  })

  const formatLocalDateTimeTooltip = (value, suffixText) => {
    if (!value) {
      return null
    }

    const numericValue = Number(value)
    const millis = Number.isFinite(numericValue) ? (numericValue < 1000000000000 ? numericValue * 1000 : numericValue) : null
    const date = millis !== null ? new Date(millis) : new Date(value)

    if (Number.isNaN(date.getTime())) {
      return suffixText || null
    }

    const formatted = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date)

    return suffixText ? `${formatted} ${suffixText}` : formatted
  }

  const couponSentTooltip = (sentAt) => formatLocalDateTimeTooltip(sentAt)

  const patientNotifiedTooltip = (record) => {
    const lastNotification = record?.patientNotifications?.[record.patientNotifications.length - 1]
    if (!lastNotification) {
      return null
    }

    if (lastNotification.datetime) {
      return lastNotification.notifiedBy ? `${lastNotification.datetime} by ${lastNotification.notifiedBy}` : lastNotification.datetime
    }

    const timestamp = lastNotification.sentAt || lastNotification.createdAt || lastNotification.timestamp
    return timestamp ? formatLocalDateTimeTooltip(timestamp, lastNotification.notifiedBy ? `by ${lastNotification.notifiedBy}` : null) : null
  }

  const providerAcknowledgedTooltip = (record) => {
    if (!record?.providerAcknowledgedAt) {
      return null
    }

    const formattedDate = formatLocalDateTimeTooltip(record.providerAcknowledgedAt)
    return record.providerAcknowledgedBy ? `${formattedDate} by ${record.providerAcknowledgedBy}` : formattedDate
  }

  return (
    <List
      {...props}
      title={providerInfo.displayName}
      filter={{ _order: 'DESC', providerId: providerInfo.id }}
      filterDefaultValues={{ showTestedKits: 'true' }}
      actions={
        <TopToolbar>
          <Box
            sx={{
              '& .add-filter': {
                color: 'var(--text-accent)',
                background: 'none',
                boxShadow: 'none',
                textTransform: 'none',
              },
              '& .add-filter:hover': { background: 'var(--fill-ghost-hover)' },
            }}
          >
            <FilterButton />
          </Box>
          <SelectColumnsButton sx={{ color: 'var(--text-accent)', textTransform: 'none' }} />
        </TopToolbar>
      }
      filters={filterArr}
      pagination={false}
      empty={<Empty text="No results found for the selected provider." />}
      sx={{
        '& .RaList-content': {
          background: 'var(--surface-2)',
          border: '0.5px solid var(--border)',
          borderRadius: '12px',
          boxShadow: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <DatagridConfigurable
        bulkActionButtons={false}
        rowStyle={resultRowStyle}
        sx={{
          '& .RaDatagrid-headerCell': {
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            textTransform: 'none',
            borderBottom: '0.5px solid var(--border)',
          },
          '& .RaDatagrid-rowCell': {
            padding: '16px',
            borderRight: 'none',
          },
          '& .RaDatagrid-row': {
            borderBottom: '0.5px solid var(--border)',
          },
          '& .RaDatagrid-row:last-of-type': {
            borderBottom: 'none',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: 'var(--fill-ghost-hover)',
          },
        }}
      >
        <FunctionField label="KitId" render={(record) => <KitIdLink record={record} />} />
        <TextField label="Patient Name" source="patientName" />
        <FunctionField render={(record) => formatDateToMMDDYYYY(record?.patientDoB)} label="Patient DOB" />
        <FunctionField label="Status" render={(record) => KitStatusLabels[record.status]} />
        <FunctionField
          label="Test Date"
          render={(record) => (isResultDetailsRestricted(record) ? '—' : record?.labTestedAt ? new Date(record.labTestedAt).toLocaleDateString() : 'Not Tested Yet')}
        />
        <FunctionField label="Result" render={(record) => labTestResultLabelWithNotes(record)} />
        <FunctionField label="Report" render={(record) => (isResultDetailsRestricted(record) ? '—' : ResultDetailPdf({ record, notify }))} />
        <FunctionField
          label="Patient Notified"
          render={(record) => (
            <span title={patientNotifiedTooltip(record)}>
              <StatusPill value={record.patientNotifications?.length > 0} />
            </span>
          )}
        />
        <FunctionField
          label="Replacement Coupon Sent"
          render={(record) => (
            <span title={couponSentTooltip(record.replacementCouponSentAt)}>
              <StatusPill value={Boolean(record.replacementCouponSentAt)} />
            </span>
          )}
        />
        <FunctionField
          label="Provider Ack'd"
          render={(record) => (
            <span title={providerAcknowledgedTooltip(record)}>
              <StatusPill value={record.providerAcknowledgedAt > 0} />
            </span>
          )}
        />
        <ViewTestResultAction label="Action" />
      </DatagridConfigurable>
    </List>
  )
}

export default ResultList
